import { useState } from 'react'
import type { PromptDraftDto, PromptSlotDto } from '@lunary/api-client'
import { ConfirmDialog } from '../../shared/ui/confirm-dialog'
import { useCreatePromptDraft, useDeletePromptDraft } from './queries'
import { PromptDraftEditor } from './prompt-draft-editor'
import styles from './studio.module.css'

type Props = {
  readonly slot: PromptSlotDto
  readonly drafts: readonly PromptDraftDto[]
  readonly viewedVersion: number
  readonly viewedContent: string
}

const DRAFT_SCOPES = ['persona', 'system'] as const
const DRAFT_NAMES = ['', 'daily-reading', 'chat'] as const

export function PromptDrafts({ slot, drafts, viewedVersion, viewedContent }: Props) {
  const create = useCreatePromptDraft()
  const remove = useDeletePromptDraft()
  const [openId, setOpenId] = useState('')
  const [deleteId, setDeleteId] = useState('')
  const open = drafts.find((d) => d.id === openId)
  const scope = DRAFT_SCOPES.find((x) => x === slot.scope)
  const name = DRAFT_NAMES.find((x) => x === slot.name)

  return (
    <section className={styles.library}>
      <div className={styles.resultHead}>
        <h2>Черновики</h2>
        <button
          disabled={create.isPending || scope === undefined || name === undefined}
          onClick={() => {
            if (scope === undefined || name === undefined) return
            create.mutate(
              { scope, name, base_version: viewedVersion, title: '', content: viewedContent },
              { onSuccess: (d) => setOpenId(d.id) },
            )
          }}
        >
          Новый черновик от v{viewedVersion}
        </button>
      </div>
      {create.error && <p className="error-box">{create.error instanceof Error ? create.error.message : 'Ошибка'}</p>}
      {drafts.length === 0 && <p className="muted">Черновиков пока нет.</p>}
      {drafts.map((d) => (
        <article key={d.id} className={styles.example}>
          <div className={styles.exampleHead}>
            <strong>{d.title || `Черновик от v${d.base_version}`}</strong>
            <span className="muted">
              от v{d.base_version} · {d.updated_by_name ?? d.author_name ?? '—'} ·{' '}
              {new Date(d.updated_at).toLocaleString('ru-RU')}
            </span>
          </div>
          <div className="row">
            <button className="secondary" onClick={() => setOpenId(openId === d.id ? '' : d.id)}>
              {openId === d.id ? 'Свернуть' : 'Открыть'}
            </button>
            <button className="danger" onClick={() => setDeleteId(d.id)}>
              Удалить
            </button>
          </div>
          {open?.id === d.id && <PromptDraftEditor key={d.id} draft={d} />}
        </article>
      ))}
      <ConfirmDialog
        open={deleteId !== ''}
        title="Удалить черновик?"
        text="Черновик пропадёт без возможности восстановить. Версии в репозитории не меняются."
        confirmLabel="Удалить"
        danger
        pending={remove.isPending}
        error={remove.error instanceof Error ? remove.error.message : null}
        onConfirm={() => remove.mutate(deleteId, { onSuccess: () => setDeleteId('') })}
        onClose={() => setDeleteId('')}
      />
    </section>
  )
}
